import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, Globe, MapPin, Camera, CheckCircle, X, Phone, Save, Info, Trash2, Users, ArrowLeft } from 'lucide-react';
import Cropper from 'react-easy-crop';
import Select from 'react-select';
import { useUser } from '../contexts/UserContext';
import { getToken } from '../utils/auth';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE = import.meta.env.VITE_API_BASE;
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

const libraries = ['places'];

// Full country list with flags and dialing codes
const COUNTRIES = [
  { name: 'United States', code: 'US', dial: '1' },
  { name: 'United Kingdom', code: 'GB', dial: '44' },
  { name: 'Israel', code: 'IL', dial: '972' },
  { name: 'Germany', code: 'DE', dial: '49' },
  { name: 'France', code: 'FR', dial: '33' },
  { name: 'Japan', code: 'JP', dial: '81' }
];

// Prepare options for react-select with small flags
const countryOptions = COUNTRIES.map(c => ({
  value: c.dial,
  label: (
    <div className="flex items-center">
      <img
        src={`https://flagcdn.com/16x12/${c.code.toLowerCase()}.png`}
        alt={c.name}
        className="w-4 h-3 mr-2"
      />
      <span className="text-sm">+{c.dial}</span>
    </div>
  )
}));

const selectStyles = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    background: 'var(--card-bg)',
    borderColor: state.isFocused ? 'var(--accent-primary)' : 'var(--border-color)',
    boxShadow: state.isFocused ? '0 0 0 1px var(--accent-primary)' : 'none',
    minHeight: '2.5rem',
    height: '2.5rem',
    minWidth: '90px',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--accent-primary)' : 'var(--border-color)'
    }
  }),
  valueContainer: baseStyles => ({
    ...baseStyles,
    height: '2.5rem',
    padding: '0 0.5rem',
    flexWrap: 'nowrap'
  }),
  singleValue: baseStyles => ({
    ...baseStyles,
    color: 'var(--text-primary)',
    fontSize: '0.875rem',
    position: 'relative',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }),
  input: baseStyles => ({
    ...baseStyles,
    margin: 0,
    padding: 0,
    color: 'var(--text-primary)'
  }),
  dropdownIndicator: baseStyles => ({
    ...baseStyles,
    padding: '0 4px',
    color: 'var(--text-primary)'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  menu: baseStyles => ({
    ...baseStyles,
    background: 'var(--card-bg)',
    color: 'var(--text-primary)'
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    background: state.isFocused ? 'var(--hover-light)' : 'var(--card-bg)',
    color: 'var(--text-primary)',
    '&:active': {
      background: 'var(--hover-dark)'
    }
  })
};

export default function CompanySettings() {
  const { userData } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [showCropper, setShowCropper] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [shouldDeleteLogo, setShouldDeleteLogo] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const fileInputRef = useRef(null);
  const mountedRef = useRef(false);
  const [autocomplete, setAutocomplete] = useState(null);

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const init = async () => {
      if (userData) {
        if (!['Owner', 'Admin'].includes(userData.permissionsId)) {
          navigate('/home');
          return;
        }
        await fetchCompanyData();
      } else {
        navigate('/login');
      }
    };
    
    init();
  }, [userData, navigate]);

  const onLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      
      // Only update if we have a valid place with address_components
      if (place.address_components) {
        let city = '';
        let country = '';

        place.address_components?.forEach(component => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('country')) {
            country = component.long_name;
          }
        });

        // Only update if we have all required components
        if (city && country && place.formatted_address) {
          setCompanyData(prev => ({
            ...prev,
            address: place.formatted_address,
            city: city,
            country: country
          }));
          
          setClearedFields(prev => ({
            ...prev,
            address: false,
            city: false,
            country: false
          }));
        } else {
          // If missing any component, clear all address fields
          clearAddressFields();
        }
      } else {
        // If no address_components, clear all address fields
        clearAddressFields();
      }
    }
  };

  const clearAddressFields = () => {
    setCompanyData(prev => ({
      ...prev,
      address: '',
      city: '',
      country: ''
    }));
    setClearedFields(prev => ({
      ...prev,
      address: Boolean(originalData?.address),
      city: Boolean(originalData?.city),
      country: Boolean(originalData?.country)
    }));
  };

  const [companyData, setCompanyData] = useState({
    name: '',
    productId: '',
    country: '',
    city: '',
    address: '',
    website: '',
    logo: null,
    logoUrl: null,
    email: '',
    phoneCountry: COUNTRIES[0].dial,
    phone: ''
  });

  // Add state to track cleared fields
  const [clearedFields, setClearedFields] = useState({
    name: false,
    country: false,
    city: false,
    address: false,
    website: false,
    logoUrl: false,
    email: false,
    phoneCountry: false,
    phone: false
  });

  const fetchCompanyData = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/company`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const newData = {
          name: data.name || '',
          productId: data.productId || '',
          country: data.country || '',
          city: data.city || '',
          address: data.address || '',
          website: data.website || '',
          logo: null,
          logoUrl: data.logoUrl || null,
          email: data.email || '',
          phoneCountry: data.phone ? (data.phoneCountry || '') : (userData?.phoneCountry || COUNTRIES[0].dial),
          phone: data.phone || ''
        };
        setCompanyData(newData);
        setOriginalData(newData);
      } else if (response.status === 401) {
        navigate('/login');
      }
    } catch (err) {
      setErrors({ general: err.message === 'Failed to fetch' ? 'Connection error. Please check your internet connection and try again.' : (err.message || 'Unable to connect to the server') });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({ ...prev, [name]: value }));
    
    // Track if field was cleared
    if (originalData && originalData[name] && value === '') {
      setClearedFields(prev => ({ ...prev, [name]: true }));
    } else {
      setClearedFields(prev => ({ ...prev, [name]: false }));
    }
    
    setErrors(prev => ({ ...prev, [name]: '' }));
    setSuccess('');
  };

  const handleLogoClick = () => {
    if (!companyData.logo) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setShouldDeleteLogo(false);
      const reader = new FileReader();
      reader.onload = () => {
        setImageUrl(reader.result);
        setShowCropper(true);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
        setCroppedAreaPixels(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCrop = async () => {
    if (!croppedAreaPixels) return;

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const image = new Image();
      
      image.src = imageUrl;
      await new Promise((resolve) => {
        image.onload = resolve;
      });

      // Set canvas size to match the cropped dimensions while maintaining max dimensions
      const maxSize = 600;
      let width = croppedAreaPixels.width;
      let height = croppedAreaPixels.height;
      
      // Scale down if either dimension exceeds maxSize while maintaining aspect ratio
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = (height / width) * maxSize;
          width = maxSize;
        } else {
          width = (width / height) * maxSize;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;

      // Draw the cropped image
      const { x, y } = croppedAreaPixels;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      ctx.drawImage(
        image,
        x * scaleX,
        y * scaleY,
        croppedAreaPixels.width * scaleX,
        croppedAreaPixels.height * scaleY,
        0,
        0,
        width,
        height
      );

      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'logo.jpg', { type: 'image/jpeg' });
          setCompanyData(prev => ({ ...prev, logo: file }));
          setShowCropper(false);
          setImageUrl('');
          setCroppedAreaPixels(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }, 'image/jpeg', 0.9);
    } catch (error) {
      // Error handling is still in place, just not logging to console
    }
  };

  const handleDeleteLogo = () => {
    setCompanyData(prev => ({ ...prev, logo: null, logoUrl: null }));
    setShouldDeleteLogo(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^\d-]/g, '');
    setCompanyData(prev => ({ 
      ...prev, 
      phone: value,
      // If phone is cleared, set phoneCountry to user's phoneCountry
      phoneCountry: value === '' ? (userData?.phoneCountry || COUNTRIES[0].dial) : prev.phoneCountry
    }));
    
    // If phone is cleared, mark both phone and phoneCountry for deletion
    if (value === '') {
      setClearedFields(prev => ({
        ...prev,
        phone: true,
        phoneCountry: true
      }));
    } else {
      setClearedFields(prev => ({
        ...prev,
        phone: false,
        phoneCountry: false
      }));
    }
    
    setErrors(prev => ({ ...prev, phone: '' }));
    setSuccess('');
  };

  // Modify the phone country select handler
  const handlePhoneCountryChange = (option) => {
    setCompanyData(prev => ({ ...prev, phoneCountry: option.value }));
    setClearedFields(prev => ({
      ...prev,
      phoneCountry: false
    }));
    setErrors(prev => ({ ...prev, phoneCountry: '' }));
    setSuccess('');
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    if (!companyData.name) {
      newErrors.name = 'Company name is required';
      isValid = false;
    }

    if (companyData.website && (companyData.website.includes(' ') || !companyData.website.includes('.'))) {
      newErrors.website = 'Please enter a valid website URL';
      isValid = false;
    }

    if (companyData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Validate address fields - if any address field is present, all must be present
    if (companyData.address || companyData.city || companyData.country) {
      if (!companyData.address || !companyData.city || !companyData.country) {
        newErrors.address = 'Please select a complete address from the suggestions';
        isValid = false;
        // Clear address fields when this error occurs
        clearAddressFields();
      }
    }

    if (companyData.phone && !/^\+?[\d\s-()]+$/.test(companyData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const hasChanges = () => {
    if (!originalData) return false;
    return JSON.stringify({
      name: companyData.name,
      country: companyData.country,
      city: companyData.city,
      address: companyData.address,
      website: companyData.website,
      email: companyData.email,
      phoneCountry: companyData.phoneCountry,
      phone: companyData.phone
    }) !== JSON.stringify({
      name: originalData.name,
      country: originalData.country,
      city: originalData.city,
      address: originalData.address,
      website: originalData.website,
      email: originalData.email,
      phoneCountry: originalData.phoneCountry,
      phone: originalData.phone
    }) || companyData.logo !== originalData.logo || shouldDeleteLogo;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !validate() || !hasChanges()) return;
    setSaving(true);
    setErrors({});
    setSuccess('');

    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      // If logo should be deleted and no new logo is being uploaded
      if (shouldDeleteLogo && !(companyData.logo instanceof File)) {
        const deleteResponse = await fetch(`${API_BASE}/company/logo`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!deleteResponse.ok) {
          throw new Error('Failed to delete logo');
        }
      }

      // Send nullify request for cleared fields
      const nullifyFields = {
        name: clearedFields.name,
        country: clearedFields.country,
        city: clearedFields.city,
        address: clearedFields.address,
        website: clearedFields.website,
        logoUrl: shouldDeleteLogo,
        email: clearedFields.email,
        phoneCountry: clearedFields.phoneCountry,
        phone: clearedFields.phone
      };

      // Only send nullify request if there are fields to nullify
      if (Object.values(nullifyFields).some(value => value)) {
        const nullifyResponse = await fetch(`${API_BASE}/company/properties`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(nullifyFields)
        });

        if (!nullifyResponse.ok) {
          throw new Error('Failed to clear company fields');
        }
      }

      const formData = new FormData();
      
      // Only append fields that have actually changed and weren't cleared
      if (companyData.name !== originalData.name && !clearedFields.name) {
        formData.append('Name', companyData.name);
      }
      if (companyData.country !== originalData.country && !clearedFields.country) {
        formData.append('Country', companyData.country);
      }
      if (companyData.city !== originalData.city && !clearedFields.city) {
        formData.append('City', companyData.city);
      }
      if (companyData.address !== originalData.address && !clearedFields.address) {
        formData.append('Address', companyData.address);
      }
      if (companyData.website !== originalData.website && !clearedFields.website) {
        formData.append('Website', companyData.website);
      }
      if (companyData.email !== originalData.email && !clearedFields.email) {
        formData.append('Email', companyData.email);
      }
      
      if (companyData.phone !== originalData.phone && !clearedFields.phone) {
        formData.append('PhoneCountry', companyData.phoneCountry);
        formData.append('Phone', companyData.phone);
      }
      
      // Handle logo separately since it's a file
      if (companyData.logo instanceof File) {
        formData.append('Logo', companyData.logo);
      }

      // Only send PUT request if there are non-cleared fields to update
      if (Array.from(formData.entries()).length > 0) {
        const response = await fetch(`${API_BASE}/company`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          if (response.status === 401) {
            navigate('/login');
            return;
          }
          const data = await response.json();
          throw new Error(data.message || 'Failed to update company details');
        }
      }

      setSuccess('Company settings updated successfully');
      
    } catch (err) {
      setErrors({ general: err.message === 'Failed to fetch' ? 'Connection error. Please check your internet connection and try again.' : (err.message || 'Unable to connect to the server') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div 
      className="relative w-full h-screen overflow-y-auto" 
      style={{ 
        scrollbarWidth: 'none', 
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <style>
        {`
          div::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      <div className="max-w-2xl mx-auto p-4 pb-8">
        <div className="flex justify-between items-center mb-8 bg-[var(--background)] py-2">
          <h1 className="text-2xl font-bold text-black">Company Details</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 pb-20">
          {/* Basic Information Section */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-6 w-6 text-[var(--text-secondary)]" />
              <h2 className="text-lg font-medium text-black">Basic Information</h2>
            </div>
            
            {/* Company Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-black mb-1">
                Company Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-[var(--text-secondary)]" />
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={companyData.name}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 bg-[var(--card-bg)] border ${
                    errors.name ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-[var(--border-color)] focus:ring-[var(--accent-primary)] focus:border-[var(--accent-primary)]'
                  } rounded-md text-black placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1`}
                  placeholder="Enter company name"
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-black mb-1">
                Website
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Globe className="h-5 w-5 text-[var(--text-secondary)]" />
                </div>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={companyData.website}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 bg-[var(--card-bg)] border ${
                    errors.website ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-[var(--border-color)] focus:ring-[var(--accent-primary)] focus:border-[var(--accent-primary)]'
                  } rounded-md text-black placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1`}
                  placeholder="https://example.com"
                />
              </div>
              {errors.website && (
                <p className="mt-1 text-sm text-red-500">{errors.website}</p>
              )}
            </div>
          </div>

          {/* Brand Section */}
          <div className="pt-6 border-t border-[var(--border-color)]">
            <div className="flex items-center gap-2 mb-4">
              <Camera className="h-5 w-5 text-[var(--text-secondary)]" />
              <h2 className="text-lg font-medium text-black">Brand</h2>
            </div>

            {/* Company Logo */}
            <div className="flex items-start gap-6">
              <div 
                className="w-32 h-32 rounded-sm bg-[var(--card-bg)] overflow-hidden shadow-lg relative group"
              >
                {companyData.logo || companyData.logoUrl ? (
                  <>
                    <img
                      src={companyData.logo ? URL.createObjectURL(companyData.logo) : companyData.logoUrl}
                      alt="Company Logo"
                      style={{ borderRadius: '0' }}
                      className="w-full h-full object-contain p-2 bg-white/5 border-0"
                    />
                    <button
                      type="button"
                      onClick={handleDeleteLogo}
                      className="absolute top-2 left-2 p-1.5 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-black/70"
                    >
                      <Trash2 className="w-5 h-5 text-white" />
                    </button>
                  </>
                ) : (
                  <div 
                    onClick={handleLogoClick}
                    className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-[var(--hover-light)] transition-colors duration-200"
                  >
                    <Camera className="w-8 h-8 text-[var(--text-secondary)] mb-2" />
                    <span className="text-sm text-[var(--text-secondary)]">Upload Logo</span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-black mb-2">Company Logo</h3>
                <p className="text-sm text-[var(--text-secondary)] mb-4">Upload your company logo. It will be displayed across the platform.</p>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
            </div>
          </div>

          {/* Contact Section */}
          <div className="pt-6 border-t border-[var(--border-color)]">
            <div className="flex items-center gap-2 mb-4">
              <Phone className="h-5 w-5 text-[var(--text-secondary)]" />
              <h2 className="text-lg font-medium text-black">Contact Information</h2>
            </div>

            {/* Email */}
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-black mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[var(--text-secondary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={companyData.email}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 bg-[var(--card-bg)] border ${
                    errors.email ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-[var(--border-color)] focus:ring-[var(--accent-primary)] focus:border-[var(--accent-primary)]'
                  } rounded-md text-black placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1`}
                  placeholder="company@example.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Phone Fields */}
            <div>
              <label className="block text-sm font-medium text-black mb-1">Phone</label>
              <div className="flex gap-2">
                <div className="shrink-0">
                  <Select
                    options={countryOptions}
                    value={countryOptions.find(o => o.value === companyData.phoneCountry) || countryOptions[0]}
                    onChange={handlePhoneCountryChange}
                    isSearchable={false}
                    styles={selectStyles}
                    className="w-32"
                    classNamePrefix="react-select"
                  />
                </div>
                <div className="relative flex-1">
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={companyData.phone}
                    onChange={handlePhoneChange}
                    className={`w-full p-2 pl-10 pr-3 h-10 border rounded bg-[var(--card-bg)] text-black focus:outline-none ${
                      errors.phone 
                        ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                        : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                    }`}
                    placeholder="Phone number"
                  />
                  <Phone className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                </div>
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
              )}
            </div>
          </div>

          {/* Location Section */}
          <div className="pt-6 border-t border-[var(--border-color)]">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-[var(--text-secondary)]" />
              <h2 className="text-lg font-medium text-black">Location</h2>
            </div>

            {/* Address with Autocomplete */}
            <div className="space-y-4">
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-black mb-1">
                  Address
                </label>
                {isLoaded ? (
                  <div className="relative">
                    <Autocomplete
                      onLoad={onLoad}
                      onPlaceChanged={onPlaceChanged}
                      options={{
                        types: ['address'],
                        componentRestrictions: { country: [] }
                      }}
                    >
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={companyData.address}
                        onChange={(e) => {
                          // Clear error when user starts typing
                          setErrors(prev => ({ ...prev, address: '' }));
                          
                          // Clear all address fields when user types
                          clearAddressFields();
                          
                          // Update the input value
                          setCompanyData(prev => ({ ...prev, address: e.target.value }));
                        }}
                        className={`w-full px-3 pr-10 py-2 bg-[var(--card-bg)] border ${
                          errors.address ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-[var(--border-color)] focus:ring-[var(--accent-primary)] focus:border-[var(--accent-primary)]'
                        } rounded-md text-black placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1`}
                        placeholder="Enter company address"
                        autoComplete="off"
                      />
                    </Autocomplete>
                    {companyData.address && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <button
                          type="button"
                          onClick={clearAddressFields}
                          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={companyData.address}
                    className="w-full px-3 py-2 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-md text-black placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
                    placeholder={loadError ? "Error loading address search..." : "Loading address search..."}
                    disabled={!isLoaded}
                    readOnly
                  />
                )}
                {errors.address && (
                  <p className="mt-1 text-sm text-red-500">{errors.address}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Country */}
                {companyData.country && (
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-black mb-1">
                      Country
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPin className="h-5 w-5 text-[var(--text-secondary)]" />
                      </div>
                      <input
                        type="text"
                        id="country"
                        name="country"
                        value={companyData.country}
                        className="w-full pl-10 pr-3 py-2 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-md text-black focus:outline-none cursor-not-allowed opacity-75"
                        readOnly
                      />
                    </div>
                  </div>
                )}

                {/* City */}
                {companyData.city && (
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-black mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={companyData.city}
                      className="w-full px-3 py-2 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-md text-black focus:outline-none cursor-not-allowed opacity-75"
                      readOnly
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-4 pt-6 mt-8">
            <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/home')}
              className="px-5 py-2 bg-[var(--card-bg)] hover:bg-[var(--hover-light)] active:bg-[var(--hover-dark)] rounded-md text-black font-medium shadow-sm transition-all duration-200 flex items-center gap-2"
            >
              <ArrowLeft size={16} className="stroke-[1.8]" />
              Return
            </button>
              <button
                type="submit"
                disabled={saving || !hasChanges()}
                className={`flex items-center px-5 py-2 rounded-md font-medium shadow-sm transition-all duration-200 ${
                  saving || !hasChanges()
                    ? 'bg-[var(--card-bg)] text-[var(--text-secondary)] opacity-50 cursor-not-allowed'
                    : 'bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-primary-hover)] active:bg-[var(--accent-primary-pressed)]'
                }`}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>

            <div className="min-h-[80px] pt-2">
              {errors.general && (
                <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                  {errors.general}
                </div>
              )}

              {success && (
                <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                  {success}
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Image Cropper Modal */}
        {showCropper && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[var(--card-bg)] p-4 rounded-lg max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-black">Edit Logo</h3>
                <button
                  onClick={() => {
                    setShowCropper(false);
                    setImageUrl('');
                  }}
                  className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-4 relative h-[300px]">
                <Cropper
                  image={imageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  showGrid={false}
                  objectFit="contain"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-black mb-1">
                  
                </label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowCropper(false);
                    setImageUrl('');
                  }}
                  className="px-4 py-2 text-[var(--text-primary)] hover:text-[var(--text-secondary)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCrop}
                  className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)]"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 